// @flow
import {
    ABSENTEE_BID_SUCCESS,
    LIVE_BID_ACCEPTED,
    LIVE_BID_RETRACTED,
    LIVE_LOT_PASSED,
    LIVE_LOT_REOPENED,
    LIVE_LOT_SOLD,
    LIVE_LOT_UNSOLD,
    LIVE_NEXT_LOT_LOADED,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
    LOG_OUT_BIDDER,
} from './actions';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchItemBidding } from '../api/biddingInfo';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { LOAD_BIDDER_BIDDING_ALL_SUCCESS } from './bidderBiddingInfoAll';
import { LOAD_BIDDER_BIDDING_UPCOMING_SUCCESS } from './bidderBiddingInfoUpcoming';
import { LOAD_CATALOG_BIDDING_SUCCESS } from './catalogBiddingInfo';
import { LOAD_HOME_DATA_SUCCESS } from './home';
import { LOAD_ITEMS_SUCCESS } from './item';
import ms from 'ms';
import { RETRACT_BID_SUCCESS } from './retractBid';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEM_BIDDING_FAIL = 'la/domain/itemBidding/LOAD_FAIL';
export const LOAD_ITEM_BIDDING_REQUEST = 'la/domain/itemBidding/LOAD_REQUEST';
export const LOAD_ITEM_BIDDING_SUCCESS = 'la/domain/itemBidding/LOAD_SUCCESS';

const REDUX_STORE_TIME_ITEM = ms('20s');

// reducer
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
    placedHighBid: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;
    let itemId;

    switch (action.type) {
        case LOAD_ITEM_BIDDING_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_ITEM_BIDDING_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_ITEM_BIDDING_SUCCESS:
        case LOAD_CATALOG_BIDDING_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            action.payload.forEach(item => {
                const previousLeadingBid = (existing[item.itemId] || {}).leadingBid || 0;
                const previousIsReserveMet = existing[item.itemId]?.isReserveMet;
                const currentItem = existing[item.itemId] || {};
                // we'll override the existing values if we get updated values since we dont always get all of them
                existing[item.itemId] = { ...currentItem, ...item };
                if (existing[item.itemId].leadingBid < previousLeadingBid) {
                    existing[item.itemId].leadingBid = previousLeadingBid;
                }
                if (previousIsReserveMet) {
                    existing[item.itemId].isReserveMet = previousIsReserveMet;
                }
                loaded[item.itemId] = time;
                loading = difference(loading, [item.itemId]);
            });
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_BIDDER_BIDDING_ALL_SUCCESS:
        case LOAD_BIDDER_BIDDING_UPCOMING_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_ITEMS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.biddingInfos) {
                action.payload.biddingInfos.forEach(item => {
                    const previousLeadingBid = (existing[item.itemId] || {}).leadingBid || 0;
                    const previousIsReserveMet = existing[item.itemId]?.isReserveMet;
                    existing[item.itemId] = { ...item };
                    if (existing[item.itemId].leadingBid < previousLeadingBid) {
                        existing[item.itemId].leadingBid = previousLeadingBid;
                    }
                    if (previousIsReserveMet) {
                        existing[item.itemId].isReserveMet = previousIsReserveMet;
                    }
                    loaded[item.itemId] = time;
                    loading = difference(loading, [item.itemId]);
                });
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            const currentLot = action.payload.currentItem || {};
            itemId = currentLot.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.leadingBid = currentLot.leadingBid;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LOG_OUT_BIDDER:
            existing = cloneDeep(state.byId);
            Object.keys(existing).forEach(key => {
                const item = existing[key];
                item.bidderHasBid = false;
                item.bidderHasHighBid = false;
                item.bidderMaxBid = 0;
            });
            return {
                ...state,
                byId: existing,
            };
        case ABSENTEE_BID_SUCCESS:
            const { isReserveMet, leadingBid } = action.payload;
            const newBidInfo = cloneDeep(state.byId[action.meta.itemId]);
            newBidInfo.bidActivityCount = !newBidInfo.bidderHasHighBid
                ? newBidInfo.bidActivityCount + 1
                : newBidInfo.bidActivityCount;
            newBidInfo.bidderBidTimestamp = action.meta.actionTime;
            newBidInfo.bidderHasBid = true;
            newBidInfo.bidderHasHighBid = action.payload.isCurrentHighBid;
            newBidInfo.bidderMaxBid = Number(action.payload.currentBid);
            newBidInfo.leadingBid = leadingBid;
            newBidInfo.isReserveMet = isReserveMet;
            return {
                ...state,
                byId: { ...state.byId, [action.meta.itemId]: newBidInfo },
                placedHighBid: Boolean(isReserveMet),
            };
        case RETRACT_BID_SUCCESS:
            const { payload: retractedItemId } = action;
            existing = cloneDeep(state.byId[retractedItemId]);
            existing.bidderHasBid = false;
            existing.bidderHasHighBid = false;
            existing.bidderMaxBid = 0;
            return {
                ...state,
                byId: { ...state.byId, [retractedItemId]: existing },
            };
        case LIVE_BID_ACCEPTED:
        case LIVE_BID_RETRACTED:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.leadingBid = action.payload.amount;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LIVE_LOT_SOLD:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.bidderHasBid = action.payload.myBid ? true : existing.bidderHasBid;
            existing.bidderHasHighBid = action.payload.myBid;
            existing.isAvailable = false;
            existing.isLocked = true;
            existing.isPassed = false;
            existing.isSold = true;
            existing.leadingBid = action.payload.amount;
            existing.salePrice = action.payload.amount;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LIVE_LOT_UNSOLD:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.isSold = false;
            existing.isAvailable = false;
            existing.isLocked = true;
            existing.isPassed = false;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LIVE_LOT_PASSED:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.isSold = false;
            existing.isAvailable = false;
            existing.isLocked = true;
            existing.isPassed = true;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LIVE_LOT_REOPENED:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.salePrice = 0;
            existing.isSold = false;
            existing.isAvailable = true;
            existing.isPassed = false;
            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        case LIVE_NEXT_LOT_LOADED:
            itemId = action.payload.itemId;

            existing = cloneDeep(state.byId[itemId] || {});
            existing.isLocked = true;

            return {
                ...state,
                byId: { ...state.byId, [itemId]: existing },
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.biddingInfo;
const globalStateSelector = (state: GlobalState) => state;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getBiddingInfo = createSelector([byIdSelector, idSelector], (byId, id) => {
    return byId[id]
        ? {
              ...byId[id],
              // Sometimes we get bidderHasBid == false and also bidderHasHighBid == true, this mitigates that
              bidderHasBid: !byId[id].bidderHasBid ? byId[id].bidderHasHighBid : byId[id].bidderHasBid,
          }
        : {
              bidActivityCount: 0,
              bidCount: 0,
              bidderBidTimestamp: 0,
              bidderHasBid: false,
              bidderHasHighBid: false,
              bidderMaxBid: 0,
              isReserveMet: false,
              itemId: id,
              leadingBid: 0,
          };
});

export const getLoadTimeForItemBidding = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isItemBiddingLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const getBiddingInfos = createSelector([globalStateSelector, idSelector], (state, itemIds) =>
    itemIds.map(id => getBiddingInfo(state, id))
);

const shouldFetchBiddingInfoForItem = createSelector(
    [idSelector, getLoadTimeForItemBidding, isItemBiddingLoading],
    (itemId, loaded, loading) => {
        if (!itemId) return false;
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME_ITEM) return false;
        return !loading;
    }
);

export const getMyBidItemIds = createSelector(byIdSelector, byId => {
    const allBidItems = Object.keys(byId)
        .map(key => byId[key])
        .filter(item => item.bidderHasBid);

    const all = allBidItems.map(item => item.itemId);
    const past = allBidItems.filter(item => !item.isAvailable).map(item => item.itemId);
    const upcoming = allBidItems.filter(item => item.isAvailable).map(item => item.itemId);
    const won = allBidItems
        .filter(item => {
            // check conditions to make sure bidder won
            return item.bidderHasBid && item.bidderHasHighBid && item.isReserveMet && item.isSold;
        })
        .map(item => item.itemId);
    const leading = allBidItems
        .filter(item => {
            return item.bidderHasBid && item.bidderHasHighBid && !item.isSold;
        })
        .map(item => item.itemId);

    return {
        all,
        leading,
        past,
        upcoming,
        won,
    };
});

export const getPlacedHighBid = createSelector(stateSelector, state => state.placedHighBid);

/* ACTION CREATORS */
export const fetchItemBiddingIfNeeded = (itemId: number, force?: boolean) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    if (force || shouldFetchBiddingInfoForItem(state, itemId)) {
        try {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({
                meta: { actionTime: Date.now() },
                payload: itemId,
                type: LOAD_ITEM_BIDDING_REQUEST,
            });
            const response = await fetchItemBidding({ authToken, deployment, itemId });
            dispatch({
                meta: { actionTime: Date.now(), itemId },
                payload: response.data,
                type: LOAD_ITEM_BIDDING_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { itemId },
                payload: error,
                type: LOAD_ITEM_BIDDING_FAIL,
            });
        }
    } else {
        return Promise.resolve({});
    }
};
